/** Small shared presentational pieces used across every page. */

import type { ReactNode } from 'react'
import { LEVELS, levelColor, type Level, type Status } from '../api'

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 text-sm text-ink-500" role="status">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-ink-600 border-t-brand-500" />
      {label ?? 'Loading…'}
    </div>
  )
}

export function LevelBadge({ level, size = 'sm' }: { level: Level | null; size?: 'xs' | 'sm' }) {
  if (!level) {
    return (
      <span
        className={`inline-flex items-center rounded-full bg-ink-700/70 font-bold text-ink-400 ${
          size === 'xs' ? 'px-1.5 py-px text-[9px]' : 'px-2 py-0.5 text-[10px]'
        }`}
        title="component / radical only, not a JLPT kanji"
      >
        —
      </span>
    )
  }
  return (
    <span
      className={`inline-flex items-center rounded-full font-bold ${
        size === 'xs' ? 'px-1.5 py-px text-[9px]' : 'px-2 py-0.5 text-[10px]'
      }`}
      style={{ color: levelColor[level], background: `${levelColor[level]}1f` }}
    >
      {level}
    </span>
  )
}

/** R = radical only, RK = both radical and kanji, C = plain component. */
export function RoleBadge({ node }: { node: { is_kanji: boolean; is_radical: boolean } }) {
  const [text, title] =
    node.is_radical && node.is_kanji
      ? ['RK', 'used as a radical and is a kanji in its own right']
      : node.is_radical
        ? ['R', 'radical form only']
        : ['C', 'component']
  return (
    <span
      className="inline-flex items-center rounded-full bg-ink-700/80 px-1.5 py-px text-[9px] font-bold text-ink-400"
      title={title}
    >
      {text}
    </span>
  )
}

export function StatusDot({ status }: { status: Status | null }) {
  if (!status) return null
  if (status === 'mastered') {
    return (
      <span
        className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-brand-500 text-[9px] font-bold text-ink-950"
        title="mastered"
      >
        ✓
      </span>
    )
  }
  const color = status === 'review' ? 'bg-n4' : 'bg-n1'
  return <span className={`h-2.5 w-2.5 rounded-full ${color}`} title={status} />
}

export function Progressbar({ value, max, color }: { value: number; max: number; color?: string }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-ink-800">
      <div
        className="h-full rounded-full transition-[width] duration-500"
        style={{ width: `${pct}%`, background: color ?? '#22c55e' }}
      />
    </div>
  )
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="rounded-xl border border-ink-700/70 bg-ink-850/70 px-3 py-2.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-500">{label}</div>
      <div className="mt-0.5 text-xl font-bold text-slate-100">{value}</div>
      {hint && <div className="text-[11px] text-ink-500">{hint}</div>}
    </div>
  )
}

export function LevelPicker({
  value,
  onChange,
  totals,
}: {
  value: Level | null
  onChange: (l: Level) => void
  totals?: Record<string, number>
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {LEVELS.map((l) => {
        const active = value === l
        return (
          <button
            key={l}
            type="button"
            onClick={() => onChange(l)}
            aria-pressed={active}
            // These pick the JLPT level for the whole screen, so they are the
            // first thing tapped on both Learn and Quiz. min-h/min-w keeps them
            // at a tappable size without stretching the row on desktop.
            className={`inline-flex min-h-[44px] min-w-[52px] items-center justify-center rounded-xl border
                        px-3 py-2 text-sm font-bold transition active:scale-[.98] ${
                          active ? 'border-transparent' : 'border-ink-700 bg-ink-850 hover:border-ink-600'
                        }`}
            style={active ? { background: `${levelColor[l]}22`, color: levelColor[l], borderColor: `${levelColor[l]}66` } : undefined}
          >
            {l}
            {totals?.[l] !== undefined && (
              <span className="ml-1.5 text-[10px] font-medium text-ink-500">{totals[l]}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}

export function EmptyState({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-ink-700 px-6 py-12 text-center">
      <div className="jp text-4xl text-ink-600">空</div>
      <div className="font-semibold text-slate-200">{title}</div>
      {body && <div className="max-w-sm text-sm text-ink-500">{body}</div>}
      {action}
    </div>
  )
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div role="alert" className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
      {children}
    </div>
  )
}

/** Deterministic avatar: a coloured tile holding the learner's initial. */
export function Avatar({ name, seed, size = 36 }: { name: string; seed: string; size?: number }) {
  const palette = ['#22c55e', '#3b82f6', '#06b6d4', '#ec4899', '#f59e0b', '#a855f7', '#ef4444']
  let h = 0
  for (const ch of seed + name) h = (h * 31 + ch.charCodeAt(0)) % 9973
  const color = palette[h % palette.length]
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-xl font-bold"
      style={{ width: size, height: size, background: `${color}26`, color, fontSize: size * 0.42 }}
      aria-hidden
    >
      {(name.trim()[0] ?? '?').toUpperCase()}
    </div>
  )
}
